/**
 * Logging something by hand, plus one-tap repeats of things logged before.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { EntryForm, emptyDraft, type Draft } from "../components/EntryForm";
import { EXTRA_NUTRIENTS } from "../../server/nutrients";

type Recent = Awaited<ReturnType<typeof api.recent>>[number];

export function Add() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [recent, setRecent] = useState<Recent[]>([]);

  useEffect(() => {
    // If this fails there is simply nothing to suggest, which is not worth
    // interrupting her with -- the form underneath works regardless.
    api.recent().then(setRecent).catch(() => {});
  }, []);

  /** Fills the form from something logged before, ready to confirm or edit. */
  function reuse(item: Recent) {
    const text = (value: number | null) => (value == null ? "" : String(value));
    const servings = item.servings || 1;
    // Stored values are the total actually eaten. The form works per serving,
    // so they are divided back out.
    const perServing = (value: number | null) =>
      value == null ? "" : String(Math.round((value / servings) * 100) / 100);

    setDraft({
      ...emptyDraft,
      kind: item.kind,
      name: item.name,
      servings: String(servings),
      calories: perServing(item.calories),
      protein_g: perServing(item.protein_g),
      carbs_g: perServing(item.carbs_g),
      fat_g: perServing(item.fat_g),
      fiber_g: perServing(item.fiber_g),
      sugar_g: perServing(item.sugar_g),
      sodium_mg: perServing(item.sodium_mg),
      ...Object.fromEntries(EXTRA_NUTRIENTS.map((n) => [n.key, perServing(item[n.key])])),
      minutes: text(item.minutes),
      barcode: item.barcode,
      source: "saved",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <>
      <h1 className="screen-title">Add</h1>

      <div className="card">
        <EntryForm draft={draft} onChange={setDraft} onSaved={(entry) => navigate("/", { state: { logged: entry } })} />
      </div>

      {recent.length > 0 && (
        <div className="card">
          <h2 style={{ fontSize: "1rem", marginBottom: 10 }}>Again?</h2>
          <p className="muted small" style={{ marginTop: 0 }}>
            Things you've logged before. Tap one to fill the form in.
          </p>
          <div className="chips">
            {recent.slice(0, 12).map((item) => (
              <button
                key={`${item.kind}:${item.name}`}
                className="chip"
                onClick={() => reuse(item)}
              >
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
